import type { Sparte } from '@/features/Global/types';

/**
 * V1-Sparte → zugehörige V2-Fake-Sparte. Einzige Quelle dieser Paarung im Frontend
 * (Pendant zu `Component\Request\SpartenAlias::ALIASES` im Backend).
 */
export const KFZ_V2_PAIRS = {
  pkw: 'pkwv2',
  krad: 'kradv2',
  nutz: 'nutzv2',
  camp: 'campv2',
} as const;

export type KfzV1Sparte = keyof typeof KFZ_V2_PAIRS;
export type KfzV2Sparte = (typeof KFZ_V2_PAIRS)[KfzV1Sparte];

export const KFZ_V1_SPARTEN = Object.keys(KFZ_V2_PAIRS) as KfzV1Sparte[];

/**
 * Welche KFZ-Version eine Sparte fährt, gesetzt über `VITE_KFZ_MODE_<SPARTE>`:
 *
 * - `V1`       – nur V1 unter `/camp`, die V2-Sparte wird nicht geroutet
 * - `PARALLEL` – V1 unter `/camp`, V2 zusätzlich unter `/campv2` (Testbetrieb)
 * - `V2`       – V2 unter `/camp`, V1 wird nicht geroutet, `/campv2/*` leitet auf `/camp/*` um
 *
 * Der Wert muss mit `KFZ_MODE_<SPARTE>` im Backend übereinstimmen: dort entscheidet er, ob
 * `/api/campv2/…` bedient wird – und diesen Pfad ruft das Frontend auch im Modus `V2`, weil der
 * Sparten-Key `campv2` bleibt und erst das Alias im API-Pfad den V2-Container wählt.
 */
export enum KFZ_MODE {
  V1 = 'v1',
  PARALLEL = 'parallel',
  V2 = 'v2',
}

/**
 * V2-KFZ-Sparte = Fake-Sparte für die Softfair-V2-Tarifberechnung. Diese Sparten bilden die
 * fachlichen V2-Deltas gegenüber ihren V1-Pendants ab – u. a. entfällt das per-Fahrer-Feld
 * `hasAnSicherheitstrainingTeilgenommen` zugunsten des VN-Datumsfelds
 * `anSicherheitstrainingTeilnahmedatum`.
 */
export const isKfzV2Sparte = (sparte: Sparte | null | undefined): boolean =>
  KFZ_V1_SPARTEN.some((v1) => KFZ_V2_PAIRS[v1] === sparte);

export const kfzV2SparteOf = (sparte: KfzV1Sparte): KfzV2Sparte =>
  KFZ_V2_PAIRS[sparte];

export const kfzV1SparteOf = (
  sparte: Sparte | null | undefined,
): KfzV1Sparte | undefined =>
  KFZ_V1_SPARTEN.find((v1) => KFZ_V2_PAIRS[v1] === sparte);

/**
 * Vite ersetzt nur statische `import.meta.env.VITE_*`-Zugriffe zur Build-Zeit, deshalb ein
 * Switch statt eines dynamischen Schlüssels. Der Zugriff liegt innerhalb der Funktion, damit
 * `vi.stubEnv` in Tests greift.
 */
const rawModeOf = (sparte: KfzV1Sparte): unknown => {
  switch (sparte) {
    case 'pkw':
      return import.meta.env.VITE_KFZ_MODE_PKW;
    case 'krad':
      return import.meta.env.VITE_KFZ_MODE_KRAD;
    case 'nutz':
      return import.meta.env.VITE_KFZ_MODE_NUTZ;
    case 'camp':
      return import.meta.env.VITE_KFZ_MODE_CAMP;
    default:
      return undefined;
  }
};

const isKfzMode = (value: string): value is KFZ_MODE =>
  Object.values<string>(KFZ_MODE).includes(value);

/**
 * Fehlender oder unbekannter Wert ergibt `V1` – ein Tippfehler in der Env darf V2 nicht
 * versehentlich aktivieren.
 */
export const kfzModeOf = (sparte: KfzV1Sparte): KFZ_MODE => {
  const raw = rawModeOf(sparte);

  if (typeof raw !== 'string') {
    return KFZ_MODE.V1;
  }

  const normalized = raw.trim().toLowerCase();

  return isKfzMode(normalized) ? normalized : KFZ_MODE.V1;
};

/**
 * Sparte, deren Config die nach aussen sichtbare Darstellung liefert – Pfad und Anzeigename.
 * Im Modus `V2` läuft die V2-Sparte unter der kanonischen URL und trägt den V1-Namen, obwohl
 * ihr Sparten-Key `campv2` bleibt.
 *
 * Wer Anzeigetexte über den Sparten-Key aus `spartenConfigModules` auflöst, muss hierüber
 * gehen; sonst zeigt er den internen „(V2)"-Namen (siehe `utils/documentTitle`). Innerhalb
 * des Routings erledigt das `router/spartenRoutesFactory/applyKfzV2Switch`.
 */
export const displaySparteOf = (sparte: Sparte): Sparte => {
  const v1Sparte = kfzV1SparteOf(sparte);

  return v1Sparte !== undefined && kfzModeOf(v1Sparte) === KFZ_MODE.V2
    ? v1Sparte
    : sparte;
};
